import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  Container,
  Spacer,
  Spinner,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DocumentType, FreightService, RequestStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { formatAwb, getPrettyRequestStatus, getPrettyShipmentStatus, modelUtils } from '@pelicargo/utils';
import { ShipmentStatus } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import z from 'zod';

import { BookingCancelledPage } from '../../components/BookingCancelledPage';
import { LinkedRequestModal } from '../../components/LinkedRequestModal';
import { ModifyButton } from '../../components/ModifyButton';
import { NewLabeledValue } from '../../components/NewLabeledValue';
import { Page } from '../../components/Page';
import { ShareButton } from '../../components/ShareButton';
import { SupportButton } from '../../components/SupportButton';
import { shipmentStatusColorScheme, statusColorScheme } from '../../config/theme';
import { trpc } from '../../config/trpc';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { CONTAINER_2XL } from '../../utils/constants';
import { AfrBookingConfirmation } from './afrBookingConfirmation';
import { ExwBookingConfirmation } from './exwBookingConfirmation';
import { FcaBookingConfirmation } from './fcaBookingConfirmation';

type Params = {
  requestId?: string;
  quoteId?: string;
};

export const QuoteConfirmationDetails = () => {
  const params: Params = useParams();
  const { targetAccount } = useTargetAccount();
  const [isBookingCancelled, setIsBookingCancelled] = useState(false);

  const {
    data: { quote } = {},
    isLoading,
    isRefetching,
    refetch,
    error,
  } = trpc.getFinalQuote.useQuery(
    { id: Number(params?.quoteId) },
    { enabled: !!params?.quoteId, refetchOnWindowFocus: false },
  );

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const schema = z.object({
    documents: z
      .array(
        z.object({
          id: z.number(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          original_filename: z.string(),
          type: z.nativeEnum(DocumentType, { invalid_type_error: 'Invalid Document Type' }).nullish(),
          request_id: z.number().nullish(),
          document_id: z.number().nullish(),
        }),
      )
      .optional(),
  });
  const request = quote?.request;
  const methods = useForm<{
    documents: Array<{
      id: number;
      created_at: Date | null;
      updated_at: Date | null;
      original_filename: string;
      type: DocumentType | null;
      request_id: number | null;
      document_id: number | null;
    }>;
  }>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      documents: request?.documents || [],
    },
  });

  useEffect(() => {
    methods.reset({
      ...methods.getValues(),
      documents: request?.documents || [],
    });
  }, [request?.documents, methods]);

  const isRequestingExw = request?.freight_services?.some((fs) => fs === FreightService.EXW);
  const isRequestingFca = request?.freight_services?.some((fs) => fs === FreightService.FCA);

  useEffect(() => {
    if (
      bookedQuoteVariant === null ||
      // Prevents flickering
      (!isNaN(bookedQuoteVariant?.quote_id) &&
        // Actual check to see if booking has been cancelled
        bookedQuoteVariant?.quote_id !== Number(params?.quoteId))
    ) {
      setIsBookingCancelled(true);
    }

    return () => {
      // Reset the state in the case that they cancel
      // and then book another one again, we want to make sure
      // the existing state doesn't persist
      setIsBookingCancelled(false);
    };
  }, [bookedQuoteVariant, quote?.request?.booked_quote_variant, quote?.request?.status, params?.quoteId]);

  useEffect(() => {
    refetch();
  }, [refetch, targetAccount?.id]);

  const alertMessage = useMemo(() => {
    if (quote?.request?.status === RequestStatus.BookingRequested) {
      return `Your booking is queued with ${quote?.provider?.name}.  You will receive an email once the flights are fully confirmed.`;
    }
    if (quote?.request?.status === RequestStatus.BookingChange) {
      return `We've received your request to edit the tender date and will let you know when the airline has confirmed the new booking.`;
    }
  }, [quote?.provider?.name, quote?.request?.status]);

  const cleanedOriginalAWB = quote?.original_customer_awb_number?.replace(/[^0-9]/g, '');
  const cleanedAWB = quote?.awb_number?.replace(/[^0-9]/g, '');
  const doBothAWBExist = !!cleanedOriginalAWB && !!cleanedAWB;
  const isAwbUpdated = cleanedOriginalAWB !== cleanedAWB && doBothAWBExist;
  const awbLabel = useMemo(() => {
    if (isAwbUpdated) {
      return (
        <Tag colorScheme="blue" size="xs" px="1" textTransform="capitalize" w="fit-content" fontSize="xs">
          Updated MAWB #
        </Tag>
      );
    }
    return 'AWB#';
  }, [isAwbUpdated]);

  const shouldShowStatus = useMemo(() => {
    return (
      !modelUtils.request.isAfrOnly(request) &&
      ![null, ShipmentStatus.PENDING as ShipmentStatus].includes(request?.shipment_status)
    );
  }, [request?.freight_services, request?.shipment_status]);

  if (isLoading || isRefetching) {
    return (
      <Page title="Confirmation" flex="1" py="24">
        <Center>
          <Spinner />
        </Center>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Confirmation" flex="1" py="24" w="full">
        <Center>
          <Alert status="error" variant="subtle" maxW="sm" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error?.message}</AlertDescription>
            </Box>
          </Alert>
        </Center>
      </Page>
    );
  }

  if (isBookingCancelled) {
    return <BookingCancelledPage requestId={params.requestId} />;
  }

  const airlineRef = bookedQuoteVariant?.airline_ref;

  return (
    <Page title="Booking Confirmation" flex="1">
      <Container w="full" py={{ base: '8', lg: '10' }} px={{ base: '4', lg: '12' }} maxW={CONTAINER_2XL}>
        <Stack direction={{ base: 'column', sm: 'row' }} justify="flex-end" w="full">
          <Tag w="fit-content" colorScheme={statusColorScheme[quote?.request?.status]} size="lg" height="full">
            <Text fontWeight="bold">Request:</Text>&nbsp;{getPrettyRequestStatus(quote?.request?.status, true)}
          </Tag>
          <Show if={shouldShowStatus}>
            <Tag
              w="fit-content"
              colorScheme={shipmentStatusColorScheme[quote?.request?.shipment_status]}
              size="lg"
              height="full"
            >
              <Text fontWeight="bold">Shipment:</Text>&nbsp;
              {getPrettyShipmentStatus(quote?.request?.shipment_status)}
            </Tag>
          </Show>
          <Spacer />
          <SupportButton requestId={quote?.request?.id} type="Booked" />
          <ShareButton requestId={quote?.request?.id} type="Booked" />
          <ModifyButton requestId={quote?.request?.id} refetchQuote={refetch} />
        </Stack>
        <Show
          if={
            quote?.request?.status === RequestStatus.BookingRequested ||
            quote?.request?.status === RequestStatus.BookingChange
          }
        >
          <Alert status="info" variant="top-accent" justifyContent="center" my="4">
            <AlertIcon />

            {alertMessage}
          </Alert>
        </Show>
        <Stack direction="column" w="full" spacing="10">
          <Stack direction="row" w="full" spacing={{ base: 5, sm: 10 }} mt="4" wrap="wrap">
            <NewLabeledValue label="Request#" value={`R${quote?.request?.id}`} />
            <Show if={!!quote?.awb_number}>
              <NewLabeledValue label={awbLabel} value={formatAwb(quote?.awb_number, '-')} />
            </Show>
            <Show if={!!airlineRef}>
              <NewLabeledValue label="Airline Reference#" value={airlineRef} />
            </Show>
            <Show if={!!quote?.request?.customer_ref}>
              <NewLabeledValue label="Your Reference#" value={quote?.request?.customer_ref} />
            </Show>
          </Stack>
          {isRequestingExw && <ExwBookingConfirmation quote={quote} />}
          {isRequestingFca && <FcaBookingConfirmation quote={quote} />}
          {!isRequestingExw && !isRequestingFca && <AfrBookingConfirmation quote={quote} />}
        </Stack>
      </Container>
      <LinkedRequestModal request={quote?.request} />
    </Page>
  );
};
